import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import { onPublishTruckData, onRealTimeEventFailed } from '../../../../../graphql/subscriptions';
import { _empty } from '../../../../../graphql/queries';
import { getUserSelector } from '../../../../../redux/reducers/reducerSlices/UserReducer';
import { getAuthReducer } from '../../../../../redux/reducers/reducerSlices/AuthReducer';
import { sagaActions } from '../../../../../sagas/sagaActions';
import { removeInterceptor } from '../../../../../redux/api/Interceptor';
import Header from '../../../Header/Header';
import Navigationbar from '../../../Navigationbar/Navigationbar';
import PODockStatusFilter from '../../Components/POFilters/PODockStatusFilter/PODockStatusFilter';
import POListFilters from '../../Components/POFilters/POListFilters/POListFilters';
import TruckSection from '../../Components/TruckSection/TruckSection';
import { getTruckSelector } from '../../../../../redux/reducers/reducerSlices/getTruckReducer';
import { getLoadSelector } from '../../../../../redux/reducers/reducerSlices/getLoadReducer';
import { getDefaultDashboardSelector } from '../../../../../redux/reducers/reducerSlices/DefaultDashboardReducer';
import { processTruckSubscriptionUpdate } from '../../../../Assests/Utility';

import './LoadManagement.css';

const LoadManagement = (props) => {
    const { themeSelected } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { selectedWarehouse, isOnline } = useSelector(getUserSelector);
    const { sessionToken } = useSelector(getAuthReducer);
    const getTruck = useSelector(getTruckSelector);
    const { truckListMasterData, arrangeOrder, arrangeKey, truckList } = getTruck;
    const { selectedLoad } = useSelector(getLoadSelector);
    const dashboard = useSelector(getDefaultDashboardSelector)
    const { isWorkDay } = dashboard

    const subscriptionRef = useRef(null);
    const failureSubscriptionRef = useRef(null);
    const isOnlineRef = useRef(isOnline);
    const intervalRef = useRef(null);
    const processingDataRef = useRef({});

    useEffect(() => {
        processingDataRef.current = {
            arrangeOrder,
            arrangeKey,
            selectedLoad,
            selectedWarehouse,
            truckListMasterData,
        };
    }, [arrangeOrder, arrangeKey, selectedLoad, selectedWarehouse, truckListMasterData]);

    const logout = () => {
        dispatch({ type: sagaActions.CLEAR_SIGNEDIN_USER });
        dispatch({ type: sagaActions.RESET_USER });
        dispatch({ type: sagaActions.CLEAR_SMART_SCHEDULE_REDUCER_DATA });
        removeInterceptor();
        dispatch({ type: sagaActions.SET_TRUCK_DATA_FROM_SUBSCRIPTION, payload: {} });
        navigate("/");
    };

    //      const heartbeat = async () => {
    //     try {
    //         await API.graphql(graphqlOperation(_empty));
    //     } catch (error) {
    //         console.warn('Heartbeat failed, attempting to reconnect...', error);
    //     }
    // };

    const subscribeToFailures = () => {
        if (failureSubscriptionRef.current) {
            failureSubscriptionRef.current.unsubscribe();
        }

        failureSubscriptionRef.current = API.graphql({
            ...graphqlOperation(onRealTimeEventFailed),
            authMode: process.env.REACT_APP_APPSYNC_AUTHENTICATIONTYPE
        }).subscribe({
            next: ({ value }) => {
                console.log("🚀 ~ Failure subscription ~ value:", value)
                dispatch({ type: sagaActions.FETCH_TRUCK_LIST, payload: '' });
            },
            error: (error) => {
                console.warn('Failure subscription error:', error);
            },
        });
    }

    const subscribe = () => {
        if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
        }

        subscriptionRef.current = API.graphql({
            ...graphqlOperation(onPublishTruckData, { IDWarehouse: selectedWarehouse?.ID }),
            authMode: process.env.REACT_APP_APPSYNC_AUTHENTICATIONTYPE
        }).subscribe({
            next: ({ value }) => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    console.log('Subscription re-established, retry stopped.');
                }
                const truckData = value.data.onPublishTruckData;
                console.log("🚀 ~ subscribe ~ truckData:", truckData)
                const {
                    arrangeOrder,
                    arrangeKey,
                    selectedLoad,
                    selectedWarehouse,
                    truckListMasterData,
                } = processingDataRef.current;

                const arrange = {
                    order: arrangeOrder,
                    key: arrangeKey
                };
                const result = processTruckSubscriptionUpdate(
                    truckData,
                    selectedWarehouse,
                    truckListMasterData,
                    arrange
                );

                if (result) {
                    dispatch({ type: sagaActions.UPDATE_TRUCK_FROM_SUBSCRIPTION, payload: result });
                    dispatch({ type: sagaActions.SET_TRUCK_DATA_FROM_SUBSCRIPTION, payload: result });
                    if (selectedLoad && selectedLoad.load_control_num === truckData.load_control_num) {
                        dispatch({ type: sagaActions.INTIATE_TRUCK_DETAILS, payload: { load_control_num: truckData.load_control_num } });
                    }
                }
            },
            error: (error) => {
                console.warn('Subscription error:', error);
            },
        });
    };

    useEffect(() => {
        if (isOnline && isWorkDay && sessionToken && selectedWarehouse?.ID) {
            subscribe();
            subscribeToFailures();
        } else if (!sessionToken) {
            logout();
        }
        // const heartbeatInterval = setInterval(heartbeat, 210000); // 3.5 minutes

        return () => {
            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
            }
            if (failureSubscriptionRef.current) {
                failureSubscriptionRef.current.unsubscribe();
            }
            dispatch({ type: sagaActions.SET_TRUCK_DATA_FROM_SUBSCRIPTION, payload: '' });
            // clearInterval(heartbeatInterval);
        };
    }, [isOnline, sessionToken, isWorkDay, selectedWarehouse?.ID]);

    const attemptReconnection = async () => {
        console.log('Attempting to re-establish subscription...');
        subscribe(); // Re-initiate the subscription

        try {
            // Probe the connection with a simple query
            await API.graphql(graphqlOperation(_empty));
            
            // If the query succeeds, the connection is live.
            console.log('Connection probe successful. Stopping retry attempts and refreshing data.');
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            // *** NEW: Fetch the latest truck list to sync state ***
            
            dispatch({ type: sagaActions.FETCH_TRUCK_LIST, payload: '' });

        } catch (error) {
            // If the query fails, the connection is not yet ready.
            console.warn('Connection probe failed. Will retry.', error);
            if (error?.errors?.[0]?.message === "Timeout disconnect") {
                console.log('Timeout detected, attempting to reconnect...');
                attemptReconnection();
            }
        }
    };

    useEffect(() => {
        if (isOnline && !isOnlineRef.current) {
            let attempts = 0;
            intervalRef.current = setInterval(() => {
                if (attempts < 3) {
                    attempts++;
                    console.log(`Reconnection attempt #${attempts}`);
                    attemptReconnection();
                } else {
                    
                    console.error('Failed to reconnect after 3 attempts. Logging out.');
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    logout();
                }
            }, 30000);
        }
        isOnlineRef.current = isOnline;

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isOnline]);

    return (
        <div className='container-fluid executor'>
            <Header {...props} />
            <Navigationbar {...props} />
            <PODockStatusFilter themeSelected={themeSelected} />
            <POListFilters themeSelected={themeSelected} />
            <TruckSection themeSelected={themeSelected} type={'IMS'} />
        </div>
    );
};

export default LoadManagement;
